import { RefObject, useCallback, useLayoutEffect, useState } from 'react';
import { useTheme } from '@rossum/ui/material';

type UseSelectionPanelOverflowBudgetParams = {
  visible: boolean;
  rootRef: RefObject<HTMLDivElement | null>;
  labelRef: RefObject<HTMLDivElement | null>;
  closeButtonRef: RefObject<HTMLButtonElement | null>;
  /** Single-side horizontal padding (e.g. the value used for `px`). Doubled internally. */
  paddingXPx?: number;
  labelGapPx?: number;
};

const useSelectionPanelOverflowBudget = ({
  visible,
  rootRef,
  labelRef,
  closeButtonRef,
  paddingXPx,
  labelGapPx,
}: UseSelectionPanelOverflowBudgetParams) => {
  const theme = useTheme();
  const [budget, setBudget] = useState(0);

  const resolvedPaddingPx = (paddingXPx ?? parseInt(theme.spacing(2), 10)) * 2;
  const resolvedGapPx = labelGapPx ?? parseInt(theme.spacing(1), 10);

  const updateBudget = useCallback(() => {
    const rootWidth = rootRef.current?.getBoundingClientRect().width ?? 0;
    const labelWidth = labelRef.current?.getBoundingClientRect().width ?? 0;
    const closeButtonWidth =
      closeButtonRef.current?.getBoundingClientRect().width ?? 0;

    const nextBudget = Math.max(
      0,
      rootWidth -
        resolvedPaddingPx -
        labelWidth -
        closeButtonWidth -
        resolvedGapPx
    );

    setBudget(prev => (Math.abs(prev - nextBudget) > 0.5 ? nextBudget : prev));
  }, [closeButtonRef, labelRef, resolvedGapPx, resolvedPaddingPx, rootRef]);

  useLayoutEffect(() => {
    if (!visible) {
      return undefined;
    }

    updateBudget();

    const observer = new ResizeObserver(() => {
      updateBudget();
    });

    if (rootRef.current) {
      observer.observe(rootRef.current);
    }

    if (labelRef.current) {
      observer.observe(labelRef.current);
    }

    return () => observer.disconnect();
  }, [labelRef, rootRef, updateBudget, visible]);

  return budget;
};

export { useSelectionPanelOverflowBudget };
